import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import {
  Accordion,
  Box,
  Col,
  Container,
  Heading3,
  Row,
  Text,
  thc,
} from '@flightstats/airframe-react';

import type { ContactPoint } from '../../types/travelerProfile';
import type { AddToast } from '../../types/airframe';
import type { SaveProfileError } from './TravelerProfileFormHandler';
import { TravelerProfileContext } from './TravelerProfileFormHandler';
import { MessageSentModal } from './MessageSentModal';
import { ErrorModal } from '../../components/cirium/ErrorModal';
import { ContactPointsDefinitions } from './contactPointsForm/ContactPointsDefinitions';
import { AccordionHead, AccordionHeadWrapper } from './contactPointsForm/AccordionAlertSwitch';
import { ContactPointsRow } from './contactPointsForm/ContactPointsRow';


type ContactPointsListProps = {
  disabled: boolean;
  contactPoints: ContactPoint[];
  onTestAlert: (addToast: AddToast, contactPoint: ContactPoint) => void;
  supportedSMSCountryCodes: string[];
  hasFormError: boolean;
  saveProfilePhoneError?: SaveProfileError;
  setSaveProfilePhoneError: (error: SaveProfileError) => void;
};

interface AccordionWrapperProps {
  hasFormError: boolean;
}

const AccordionWrapper = thc<AccordionWrapperProps>(
  (props) => <Accordion {...props} />,
  {
    display: 'block',
    width: '100%',
    marginBottom: '2rem',
  },
  (props: AccordionWrapperProps) => ({
    '& button[role="tab"]:hover': {
      cursor: props.hasFormError ? 'not-allowed' : 'pointer',
    },
    '& button[role="tab"], & button[role="tab"]:active': {
      boxShadow: '0 0 0 0 transparent',
      transform: 'translateY(0)',
    },
    '& button[role="tab"] svg[alt*="chevron"]': {
      width: '24px',
    },
    '& button[role="tab"] svg[alt*="chevron"] path, & button[role="tab"]:hover > svg[alt*="chevron"] path': {
      fill: props.hasFormError ? 'gray' : '#001E60',
    },
    '& label': {
      margin: '4px 0 0',
    },
    '& [aria-expanded="true"] > [aria-hidden="false"]': {
      overflow: 'visible',
    },
  }),
);

//ContactPointsForm>ContactPointsList
const ContactPointsList: React.FC<ContactPointsListProps> = ({
  disabled,
  contactPoints,
  onTestAlert,
  supportedSMSCountryCodes,
  hasFormError,
  saveProfilePhoneError,
  setSaveProfilePhoneError,
}) => {
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setExpanded(hasFormError);
  }, [hasFormError]);

  const ContactPoints = contactPoints.map(
    (contactPoint: ContactPoint, idx: number) => {
      const [isDisabled, setIsDisabled] = useState(
        contactPoint.isDisabled || false,
      );

      return {
        head: (
          <AccordionHeadWrapper>
            <AccordionHead
              alertType={contactPoint.alertType}
              isDisabled={isDisabled}
              setIsDisabled={setIsDisabled}
            />
          </AccordionHeadWrapper>
        ),
        content: (
          <ContactPointsRow
            saveProfilePhoneError={saveProfilePhoneError}
            setSaveProfilePhoneError={setSaveProfilePhoneError}
            key={idx}
            disabled={disabled || isDisabled}
            contactPoint={contactPoint}
            onTestAlert={onTestAlert}
            supportedSMSCountryCodes={supportedSMSCountryCodes}
            isDisabled={isDisabled}
            setIsDisabled={setIsDisabled}
          />
        ),
        key: idx,
      };
    },
  );

  return (
    <Box className='contact-points-list-box'>
      <Container fluid>
        <Row>
          <Col>
          <Text mb={4} fontWeight="bold">
              Alerting Type
            </Text>
          </Col>

          <Col align="flex-end">
            <Text className='contact-points-list-text'>
              Activate Alert
            </Text>
          </Col>
        </Row>
      </Container>
      <AccordionWrapper
      arrow="left"
      items={ContactPoints}
      duration={0}
      zebraStripes
      mx="auto"
      mb="2rem"
      multiple
      disableAnimation={true}
      expanded={expanded}
      hasFormError={hasFormError}
      />
    </Box>
  );
};

type ContactPointsFormProps = {
  disabled: boolean;
  contactPoints: ContactPoint[];
  onTestAlert: (addToast: AddToast, contactPoint: ContactPoint) => void;
  testAlertSent: boolean;
  onCloseTestAlertSent: () => void;
  testAlertSentContact?: string;
  supportedSMSCountryCodes: string[];
  hasFormError: boolean;
  testAlertError: string[];
  saveProfilePhoneError?: SaveProfileError;
  setSaveProfilePhoneError: (error: SaveProfileError) => void;
};

export const ContactPointsForm: React.FC<ContactPointsFormProps> = ({
  disabled,
  contactPoints,
  onTestAlert,
  testAlertSent,
  onCloseTestAlertSent,
  testAlertSentContact,
  supportedSMSCountryCodes,
  hasFormError,
  testAlertError,
  saveProfilePhoneError,
  setSaveProfilePhoneError,
}) => {
  const messageSentModalTitle = 'Test Message Sent';
  const testAlertFailedValidationTitle = 'Error Sending Test Message';

  const { isAgent } = useContext(TravelerProfileContext);

  return (
    <>
      <Heading3 spacing={2}>Contact Points</Heading3>
      <ContactPointsDefinitions contactPoints={contactPoints} />
      <ContactPointsList
        saveProfilePhoneError={saveProfilePhoneError}
        setSaveProfilePhoneError={setSaveProfilePhoneError}
        disabled={disabled}
        contactPoints={contactPoints}
        onTestAlert={onTestAlert}
        supportedSMSCountryCodes={supportedSMSCountryCodes}
        hasFormError={hasFormError}
      />
      <MessageSentModal
        showModal={testAlertSent}
        closeModal={() => onCloseTestAlertSent()}
        title={messageSentModalTitle}
        contact={testAlertSentContact || ''}
      />
      <ErrorModal
        showModal={!_.isEmpty(testAlertError)}
        closeModal={() => onCloseTestAlertSent()}
        title={testAlertFailedValidationTitle}
        errors={testAlertError}
      />
    </>
  );
};
